import { DetailsList } from "react-migration/components/DetailsList";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { CompanySection } from "../CompanySection";
import { LoadingPanel } from "../LoadingPanel";
import { TitlesSection } from "../TitlesSection";
import { useCompanyUltimateOwnersQuery } from "../useCompanyUltimateOwnersQuery";
import { CompanyOwner, PersonOwner, isCompanyOwner, isPersonOwner } from "../useSelectedOwner";
import { Title } from "react-migration/domains/ownership/typings/Title";
import { PurchaseAllTitles } from "../PurchaseAllTitles";

type OwnerDetailPageProps = {
  titles: Title[];
  selectedOwner?: CompanyOwner | PersonOwner;
  selectedTitleNumber?: string;
};

export function OwnerDetailPage({ titles, selectedOwner, selectedTitleNumber }: OwnerDetailPageProps) {
  return (
    <div className="bg-white divide-y divide-border-base-divider">
      {isCompanyOwner(selectedOwner) && <CompanyOwnership companyOwner={selectedOwner} />}
      {isPersonOwner(selectedOwner) && <PersonalOwnership personOwner={selectedOwner} />}

      {!isCompanyOwner(selectedOwner) && titles.length > 1 && <PurchaseAllTitles titles={titles} />}

      <TitlesSection titles={titles} selectedTitleNumber={selectedTitleNumber} />
    </div>
  );
}

interface CompanyOwnershipProps {
  companyOwner: CompanyOwner;
}

function CompanyOwnership({ companyOwner }: CompanyOwnershipProps) {
  const { t } = useTranslation();
  const { company, ultimateOwner, loading } = useCompanyUltimateOwnersQuery(companyOwner.company.company_no);

  // companyMeta.company can be null, as can the company coming from the ultimateOwnerQuery
  // we only want to show the owner details if one is not null.
  const mergedCompany = (companyOwner || company) && Object.assign({}, companyOwner, company);

  if (loading) {
    return <LoadingPanel />;
  }

  return (
    <>
      {mergedCompany && (
        <CompanySection company={mergedCompany} proprietorshipAdded={companyOwner.date_proprietor_added} />
      )}
      {ultimateOwner && (
        <CompanySection
          heading={t("ownership.react.map.sidebar.ultimate_owner_details.heading")}
          company={ultimateOwner}
        />
      )}
    </>
  );
}

interface PersonalOwnershipProps {
  personOwner: PersonOwner;
}

function PersonalOwnership({ personOwner }: PersonalOwnershipProps) {
  const { person } = personOwner;
  const { t } = useTranslation();

  return (
    <div className="p-2 space-y-2">
      <div className="mb-3 text-sm font-semibold text-text-base-tertiary">
        {t("ownership.react.map.sidebar.individual_owner")}
      </div>
      <DetailsList>
        <DetailsList.Item label="Name">{person.name}</DetailsList.Item>
        <DetailsList.Item label="Address">{person.address}</DetailsList.Item>
      </DetailsList>
    </div>
  );
}
